import BpkButton from '@skyscanner-internal/backpack-web/bpk-component-button';
import {
  BpkSpacing,
  BpkStack,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { addDays, differenceInCalendarDays } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { useLocalisation } from '../../../../services/contextAdaptor/LocalisationContext';
import { hotelSelectedResult } from '../../actions/result-select-actions';
import {
  CTA_VERTICAL,
  FLIGHTS_TAB,
} from '../../helpers/constants/analyticsConstants';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import { trackInteractionEvent } from '../../helpers/logging/trackInteractionEvent';
import useBehaviourTracking from '../../middleware/trackBehaviour/hooks';
import {
  trackCTASmartMetric,
  trackHotelsCTA,
} from '../../middleware/trackEvent';
import './TotalTripCostHotelSummary.scss';
import {
  getCheapestHotelsPriceFromSelection,
  getDestinationEntityId,
  getDestinationId,
  getSelectedHotelCheckInDate,
  getSelectedHotelCheckOutDate,
  getStay,
  getTotalHotelsPriceFromSelection,
} from '../../selectors';

import type { HotelsCTAEvent } from '../../middleware/trackEvent/trackHotelsCTA';
import type { State } from '../../selectors/types';

const HotelPrice = ({ priceFormatted }: { priceFormatted: string }) => {
  const translate = useTranslation();

  return (
    <BpkStack direction="column" align="flex-end" gap={BpkSpacing.None}>
      <BpkText textStyle={TEXT_STYLES.label1}>{priceFormatted}</BpkText>
      <BpkText
        textStyle={TEXT_STYLES.footnote}
        className="month-view-variant-total-trip-cost__hotel-price-description"
        color={TEXT_COLORS.textSecondary}
      >
        {translate('per_night_label')}
      </BpkText>
    </BpkStack>
  );
};

const HotelsDescription = ({
  days,
  totalAccommodation,
}: {
  days: number;
  totalAccommodation: string | null;
}) => {
  const translate = useTranslation();

  if (!days) return null;

  const nightsKey = days === 1 ? 'ktxt_hotels_night' : 'ktxt_hotels_nights';

  return (
    <BpkText
      textStyle={TEXT_STYLES.footnote}
      className="month-view-variant-total-trip-cost__hotel-details-description"
      color={TEXT_COLORS.textSecondary}
    >
      {translate(nightsKey, { number: String(days) })}
      {totalAccommodation && (
        <>
          {' • '}
          {translate('ktxt_hotels_total', { price: totalAccommodation })}
        </>
      )}
    </BpkText>
  );
};

const TotalTripCostHotelSummary = ({ isReturn }: { isReturn: boolean }) => {
  const translate = useTranslation();
  const dispatch = useDispatch();
  const { currency, formatCurrency, locale, market } = useLocalisation();

  const checkinDate = useSelector(getSelectedHotelCheckInDate);
  const checkoutDateRaw = useSelector(getSelectedHotelCheckOutDate);
  const destinationId = useSelector(getDestinationId);
  const destinationEntityId = useSelector(getDestinationEntityId);
  const stay = useSelector(getStay);
  const price = useSelector((state: State) =>
    getCheapestHotelsPriceFromSelection(state, checkinDate, checkoutDateRaw),
  );
  const totalHotelsPrice = useSelector((state: State) =>
    checkoutDateRaw
      ? getTotalHotelsPriceFromSelection(state, checkinDate, checkoutDateRaw)
      : 0,
  );

  const groupAriaLabel = translate(
    'ktxt_Cheapest_4_star_from_price_per_night',
    {
      price: price ? formatCurrency(price) : '',
    },
  );

  const days = checkoutDateRaw
    ? differenceInCalendarDays(checkoutDateRaw, checkinDate)
    : 0;
  const checkoutDate =
    checkoutDateRaw && days > 0 ? checkoutDateRaw : addDays(checkinDate, 1);

  const priceFormatted = price ? formatCurrency(price) : null;
  const totalHotelPriceFormatted = totalHotelsPrice
    ? formatCurrency(totalHotelsPrice)
    : null;

  const impressionId = uuid();

  const trackingData: HotelsCTAEvent = {
    impression_id: impressionId,
    culture_settings: {
      country: market,
      locale,
      currency,
    },
    destination: {
      location_attribute: {
        location_attribute_encoding: 'ENTITY_ID',
        location_id: destinationEntityId,
      },
    },
    checkin_date: {
      year: checkinDate.getFullYear(),
      month: checkinDate.getMonth() + 1,
      day: checkinDate.getDate(),
    },
    checkout_date: {
      year: checkoutDate.getFullYear(),
      month: checkoutDate.getMonth() + 1,
      day: checkoutDate.getDate(),
    },
    number_of_adults: stay?.adults || 1,
    children_ages: stay?.childrenAges || [],
    number_of_rooms: 1,
    filters: {},
    cheapest_price: {
      amount: price,
      currency,
    },
    total_price: {
      amount: totalHotelsPrice,
      currency,
    },
  };

  const onResultConfirmed = (event: React.MouseEvent<HTMLButtonElement>) => {
    trackHotelsCTA(trackingData);
    trackCTASmartMetric(CTA_VERTICAL.HOTELS);
    event.preventDefault();
    event.stopPropagation();
    dispatch(
      hotelSelectedResult(
        checkinDate,
        checkoutDate,
        destinationId,
        impressionId,
      ),
    );
    trackInteractionEvent({
      name: 'Hotels_CTA_Clicked',
    });
  };

  const { ref } = useBehaviourTracking({
    componentName: 'month_view_explore_card',
    vertical: 'hotel',
    calendarTab: FLIGHTS_TAB,
  });

  const ctaButton = (
    <BpkStack
      direction="column"
      gap={BpkSpacing.None}
      ref={ref}
      data-testid="hotel-cta"
      width="100%"
      padding={BpkSpacing.XS}
    >
      <BpkButton
        className="month-view-variant-total-trip-cost__hotel-cta-button"
        onClick={onResultConfirmed}
        data-newrelic-synthetic="mvp-hotels-cta"
        data-testid="hotels-cta"
      >
        {translate('explore_hotels_text')}
      </BpkButton>
    </BpkStack>
  );

  return (
    <BpkStack direction="column" gap={BpkSpacing.None}>
      <BpkStack
        direction="row"
        justify="space-between"
        align="center"
        gap={BpkSpacing.None}
        wrap={{ base: 'wrap', tablet: 'nowrap' }}
        paddingTop={BpkSpacing.Base}
        aria-label={groupAriaLabel}
        role="group"
      >
        <BpkStack
          direction="column"
          flexGrow={1}
          gap={BpkSpacing.None}
          paddingEnd={BpkSpacing.Base}
        >
          <BpkText textStyle={TEXT_STYLES.label1}>
            {translate('ktxt_hotels_4stars')}
          </BpkText>
          {isReturn && (
            <HotelsDescription
              days={days}
              totalAccommodation={totalHotelPriceFormatted}
            />
          )}
        </BpkStack>

        <BpkStack direction="row" align="center" gap={BpkSpacing.None}>
          {totalHotelPriceFormatted && priceFormatted && isReturn && (
            <HotelPrice priceFormatted={priceFormatted} />
          )}
        </BpkStack>
      </BpkStack>
      {ctaButton}
    </BpkStack>
  );
};

export default TotalTripCostHotelSummary;
